import React, { useEffect, useMemo, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { AppBar } from "../../../components/mobileUi";
import { DetailTabBar } from "../../../components/financeDetail";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../../components/desktopUi";
import {
  Avatar,
  CenterSpinner,
  EmptyText,
  M,
  PropCell,
} from "../../../components/managementUi";
import { resolveImageUrl } from "../../../utils/format";
import { EMP_DEPARTMENT } from "../../../utils/managerRole";
import { getStaffEmployeeInfoApi } from "../../../api/staffManager.api";
import { EMP_API_BASE_URL } from "../../../config";

const fmtDate = (v) => (v ? moment(v).format("DD/MM/YYYY") : "-");

// Khớp getGenderText của app: 0 = Nam, 1 = Nữ, 2 = Khác
const genderText = (g) => {
  if (Number(g) === 0) return "Nam";
  if (Number(g) === 1) return "Nữ";
  if (Number(g) === 2) return "Khác";
  return "-";
};

// Khớp getDepartment của app
const departmentText = (isRelative, department) => {
  if (isRelative) return "CTV";
  if (Number(department) === 0) return "Hỗ trợ kinh doanh";
  if (Number(department) === 1) return "Kinh doanh";
  return "-";
};

const TABS = [
  { key: "overview", title: "Tổng quan" },
  { key: "contact", title: "Thông tin liên hệ" },
  { key: "document", title: "Thông tin giấy tờ" },
  { key: "collaborator", title: "Danh sách CTV" },
];

/**
 * Header tên + mã nhân viên (khớp StaffEmployeeInfoInfoHeader của app).
 * App luôn chừa chỗ 44px cho avatar (gap 16) nhưng chỉ vẽ ảnh khi có avatar,
 * nên khi không có ảnh phần chữ vẫn thụt vào đúng như app - không dùng ảnh mặc định.
 */
const AvatarSlot = ({ src }) => (
  <Flex w="44px" h="44px" align="center" justify="center" flexShrink={0}>
    {src ? <Avatar src={src} size={50} /> : null}
  </Flex>
);

const InfoHeader = ({ detail }) => (
  <Flex align="center" gap="16px">
    <AvatarSlot src={detail?.avatar ? resolveImageUrl(detail.avatar, EMP_API_BASE_URL) : ""} />
    <Box flex="1" minW="0">
      <Text fontSize="14px" fontWeight={600} color={M.baseContent}>
        {detail?.fullName || "-"}
      </Text>
      <Box h="4px" />
      <Text fontSize="12px" fontWeight={500} color={M.baseContent}>
        {detail?.code || "-"}
      </Text>
    </Box>
  </Flex>
);

const StaffEmployeeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [tab, setTab] = useState(0);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getStaffEmployeeInfoApi(id)
      .then((d) => setDetail(d || null))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [id]);

  const activeKey = useMemo(() => TABS[tab]?.key, [tab]);

  const renderTab = () => {
    if (loading) return <CenterSpinner />;
    if (!detail) return <EmptyText text="Không tìm thấy thông tin" />;

    if (activeKey === "overview") {
      const working = !detail.status;
      return (
        <Box pt="16px">
          <InfoHeader detail={detail} />
          <PropCell label="Chức danh" value={detail?.jobTitle?.name || "-"} />
          <PropCell label="Chức vụ" value={detail?.jobPosition?.name || "-"} />
          <PropCell label="Giới tính" value={genderText(detail?.gender)} />
          <PropCell
            label="Trạng thái hoạt động"
            value={working ? "Đang làm việc" : "Đã nghỉ việc"}
            color={working ? M.green300 : M.red}
          />
          <PropCell
            label="Trạng thái CCMG"
            value={detail?.isBrokerDebtCertificate ? "Đã có" : "Chưa có"}
            color={!detail?.isBrokerDebtCertificate ? M.green300 : M.red}
          />
          <PropCell label="Bộ phận" value={departmentText(detail?.isRelative, detail?.department)} />
          <PropCell label="Chi nhánh" value={detail?.branch?.name || "-"} />
          <PropCell label="Chi nhánh" value={detail?.branch?.name || "-"} />
          {detail?.department === EMP_DEPARTMENT.SUPPORT_BUSINESS && (
            <PropCell label="Phòng" value={detail?.supportDepartment?.name || "-"} />
          )}
          {detail?.department === EMP_DEPARTMENT.BUSINESS && (
            <PropCell label="Khối" value={detail?.saleBlock?.name || "-"} />
          )}
          {detail?.department === EMP_DEPARTMENT.BUSINESS && (
            <PropCell label="Vùng" value={detail?.saleBlock?.name || "-"} />
          )}
          <PropCell label="Chi nhánh" value={detail?.branch?.name || "-"} />
          <PropCell label="Ngày sinh" value={fmtDate(detail?.dateOfBirth)} />
          <PropCell label="Ngày vào làm" value={fmtDate(detail?.startTime)} />
        </Box>
      );
    }

    if (activeKey === "contact") {
      return (
        <Box>
          <PropCell label="Email" value={detail?.email || "-"} />
          <PropCell label="Số điện thoại" value={detail?.phone || "-"} isCopy />
          <PropCell label="Chỗ ở hiện tại" value={detail?.currentAddress || "-"} />
        </Box>
      );
    }

    if (activeKey === "document") {
      return (
        <Box>
          <PropCell label="Số CCCD" value={detail?.identificationNumber || "-"} />
          <PropCell label="Ngày cấp" value={fmtDate(detail?.dateOfIdentification)} />
          <PropCell label="Nơi cấp" value={detail?.placeOfIdentification || "-"} />
          <PropCell label="Địa chỉ thường trú" value={detail?.permanentAddress || "-"} />
          <PropCell label="Chỗ ở hiện tại" value={detail?.currentAddress || "-"} />
          <PropCell label="Mã số thuế" value={detail?.taxCode || "-"} />
        </Box>
      );
    }

    const relatives = detail?.relatives || [];
    if (!relatives.length) return <EmptyText text="Không có CTV" />;
    return (
      <Flex direction="column" gap="10px" pt="16px">
        {relatives.map((c, i) => (
          <Flex key={c?._id || i} align="center" gap="16px">
            <AvatarSlot src={c?.avatar ? resolveImageUrl(c.avatar, EMP_API_BASE_URL) : ""} />
            <Box flex="1" minW="0">
              <Text fontSize="14px" fontWeight={600} color={M.baseContent}>
                {c?.fullName || "-"}
              </Text>
              <Box h="4px" />
              <Text fontSize="12px" fontWeight={500} color={M.baseContent}>
                {c?.code || "-"}
              </Text>
            </Box>
          </Flex>
        ))}
      </Flex>
    );
  };

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage
        title="Thông tin nhân viên"
        right={<DesktopBack onClick={() => navigate(-1)} />}
      >
        <DesktopPanel p="0">
          <DetailTabBar tabs={TABS} index={tab} onChange={setTab} />
          <Box px="24px" pb="24px">
            {renderTab()}
          </Box>
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Thông tin" />
      <DetailTabBar tabs={TABS} index={tab} onChange={setTab} />
      <Box px="16px">{renderTab()}</Box>
    </Box>
  );
};

export default StaffEmployeeDetail;
